import { useRef } from 'react'
import {
  LOADER_DELAY_MS,
  LoadingSpin,
  LoadingVeil,
} from './storiesLoader.style'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'

interface IStoriesLoadingFrameProps {
  /** when the wait began (Date.now()) */
  since: number
  /** a screen with nothing else to say reads this out; in the viewer it stays silent */
  label?: string
}

/**
 * The design's loading frame: 80% black over the clip's frame and what is
 * drawn on it, and the Loader. It comes in once the wait reaches
 * LOADER_DELAY_MS, counted from `since`: the loading screen and the viewer
 * that takes over from it draw one frame, with no jump in the fade or the turn.
 */
export const StoriesLoadingFrame = ({
  since,
  label,
}: IStoriesLoadingFrameProps) => {
  const veilRef = useRef<HTMLDivElement>(null)
  const spinRef = useRef<HTMLSpanElement>(null)
  // set at the commit, where the animations start (not at the render, which
  // can come earlier), and on the elements: the overlay draws again several
  // times a second and must not move them
  useIsomorphicLayoutEffect(() => {
    const waited = Math.max(0, Date.now() - since)
    if (veilRef.current) {
      veilRef.current.style.animationDelay = `${LOADER_DELAY_MS - waited}ms`
    }
    if (spinRef.current) {
      spinRef.current.style.animationDelay = `${-waited}ms`
    }
  }, [since])

  return (
    <LoadingVeil
      ref={veilRef}
      // the QA finds it by this
      data-loading-frame=''
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : 'true'}
    >
      <LoadingSpin ref={spinRef} />
    </LoadingVeil>
  )
}
